import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

interface AddressComponents {
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
}

interface AddressAutocompleteProps {
  onAddressSelect: (components: AddressComponents) => void;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

const AddressAutocomplete = ({ onAddressSelect, value, onChange, error }: AddressAutocompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
    
    if (!apiKey) {
      console.warn("Google Places API key not configured");
      return;
    }

    // Charger l'API Google Places
    const loadGooglePlaces = () => {
      if ((window as any).google?.maps?.places) {
        initAutocomplete();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => initAutocomplete();
      document.head.appendChild(script);
    };

    const initAutocomplete = () => {
      setIsLoaded(true);
      
      if (!inputRef.current || !(window as any).google?.maps?.places) return;

      const autocomplete = new (window as any).google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: ["ca", "us"] }, // Canada et États-Unis
        fields: ["address_components", "formatted_address"],
        types: ["address"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        
        if (!place.address_components) return;

        const components: AddressComponents = {
          streetAddress: "",
          city: "",
          province: "",
          postalCode: "",
          country: "",
        };

        // Extraire les composants d'adresse
        place.address_components.forEach((component: any) => {
          const types = component.types;

          if (types.includes("street_number")) {
            components.streetAddress = component.long_name + " ";
          }
          if (types.includes("route")) {
            components.streetAddress += component.long_name;
          }
          if (types.includes("locality")) {
            components.city = component.long_name;
          }
          if (types.includes("administrative_area_level_1")) {
            components.province = component.long_name;
          }
          if (types.includes("postal_code")) {
            components.postalCode = component.long_name;
          }
          if (types.includes("country")) {
            components.country = component.long_name;
          }
        });

        onAddressSelect(components);
      });
    };

    loadGooglePlaces();
  }, [onAddressSelect]);

  return (
    <div>
      <label htmlFor="streetAddress" className="block text-sm font-medium mb-1">
        Rue * {isLoaded && <span className="text-xs text-muted-foreground">(Commencez à taper pour des suggestions)</span>}
      </label>
      <div className="relative">
        <Input
          ref={inputRef}
          id="streetAddress"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          className={`pl-10 ${error ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
          placeholder="123 rue Principale"
          maxLength={100}
        />
        <MapPin className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default AddressAutocomplete;
